import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import {
  composeFamilyAccountDisplayNameFromStudentFamilyName,
  composeStudentDisplayNameFromParts,
  normalizeStudentNamePart,
  type AdminMembersResponseDto,
  type AdminStudentsResponseDto,
  type CreateAdminStudentRequestDto,
  type CreateFamilyAccountRequestDto,
  type FamilyAccountsResponseDto,
  type FamilyAccountStatus,
  type StudentStatus
} from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchAdminMembers } from "../../shared/api/adminDirectory";
import {
  createAdminStudent,
  createFamilyAccount,
  fetchAdminStudents,
  fetchFamilyAccounts
} from "../../shared/api/adminRoster";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus } from "../../shared/lib/uiLabels";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const adminLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/enrollments", label: "所属" },
  { to: "/t/:tenantSlug/teacher/admin/guardian-links", label: "保護者紐付け" }
];

type FamilyAccountFormState = {
  displayName: string;
  primaryContactPlatformUserId: string;
  status: FamilyAccountStatus;
};

type StudentFormState = {
  familyName: string;
  givenName: string;
  displayNameKana: string;
  birthDate: string;
  status: StudentStatus;
  familyAccountMode: "createNewFamilyAccount" | "useExistingFamilyAccount" | "none";
  familyAccountId: string;
  createFamilyAccountDisplayName: string;
  notesInternal: string;
};

function createInitialFamilyAccountFormState(): FamilyAccountFormState {
  return {
    displayName: "",
    primaryContactPlatformUserId: "",
    status: "active"
  };
}

function createInitialStudentFormState(): StudentFormState {
  return {
    familyName: "",
    givenName: "",
    displayNameKana: "",
    birthDate: "",
    status: "active",
    familyAccountMode: "createNewFamilyAccount",
    familyAccountId: "",
    createFamilyAccountDisplayName: "",
    notesInternal: ""
  };
}

function getMemberDisplayName(member: AdminMembersResponseDto["items"][number]): string {
  return member.appNickname ?? member.lineDisplayNameSnapshot ?? member.platformUserId;
}

function getStudentDisplayNamePreview(form: StudentFormState): string {
  return composeStudentDisplayNameFromParts(form.familyName, form.givenName) ?? "";
}

function buildCreateStudentRequest(form: StudentFormState): CreateAdminStudentRequestDto {
  const displayName = getStudentDisplayNamePreview(form);

  return {
    displayName,
    familyName: normalizeStudentNamePart(form.familyName),
    givenName: normalizeStudentNamePart(form.givenName),
    displayNameKana: form.displayNameKana.trim() || null,
    birthDate: form.birthDate,
    status: form.status,
    familyAccountId: form.familyAccountMode === "useExistingFamilyAccount" ? form.familyAccountId || null : null,
    createFamilyAccount:
      form.familyAccountMode === "createNewFamilyAccount"
        ? {
            displayName: form.createFamilyAccountDisplayName.trim(),
            status: "active"
          }
        : null,
    notesInternal: form.notesInternal.trim() || null
  };
}

function buildCreateFamilyAccountRequest(form: FamilyAccountFormState): CreateFamilyAccountRequestDto {
  return {
    displayName: form.displayName.trim(),
    primaryContactPlatformUserId: form.primaryContactPlatformUserId || null,
    status: form.status
  };
}

export function AdminStudentsPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [students, setStudents] = useState<AdminStudentsResponseDto | null>(null);
  const [familyAccounts, setFamilyAccounts] = useState<FamilyAccountsResponseDto | null>(null);
  const [members, setMembers] = useState<AdminMembersResponseDto | null>(null);
  const [studentForm, setStudentForm] = useState<StudentFormState>(createInitialStudentFormState());
  const [familyAccountForm, setFamilyAccountForm] = useState<FamilyAccountFormState>(
    createInitialFamilyAccountFormState()
  );
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [hasEditedCreateFamilyAccountDisplayName, setHasEditedCreateFamilyAccountDisplayName] = useState(false);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;

  async function refreshPage() {
    setLoadState("loading");
    setLoadError(null);

    try {
      const [nextStudents, nextFamilyAccounts, nextMembers] = await Promise.all([
        fetchAdminStudents(tenantSlug),
        fetchFamilyAccounts(tenantSlug),
        fetchAdminMembers(tenantSlug)
      ]);

      setStudents(nextStudents);
      setFamilyAccounts(nextFamilyAccounts);
      setMembers(nextMembers);
      setLoadState("idle");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadState("error");
      setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.student}・${tenantLabels.familyAccount}の取得に失敗しました`));
    }
  }

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    void refreshPage();
  }, [hasAdminRole, isSessionReady, tenantSlug]);

  useEffect(() => {
    if (hasEditedCreateFamilyAccountDisplayName) {
      return;
    }

    setStudentForm((current) => {
      if (current.familyAccountMode !== "createNewFamilyAccount") {
        return current;
      }

      const nextDisplayName =
        composeFamilyAccountDisplayNameFromStudentFamilyName(current.familyName) ?? "";

      return nextDisplayName === current.createFamilyAccountDisplayName
        ? current
        : {
            ...current,
            createFamilyAccountDisplayName: nextDisplayName
          };
    });
  }, [hasEditedCreateFamilyAccountDisplayName, studentForm.familyName, studentForm.familyAccountMode]);

  async function handleCreateFamilyAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusyKey("create-family-account");
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const created = await createFamilyAccount(tenantSlug, buildCreateFamilyAccountRequest(familyAccountForm));
      setFamilyAccountForm(createInitialFamilyAccountFormState());
      setFeedbackMessage(`家族アカウントを作成しました: ${created.displayName}`);
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "家族アカウントの作成に失敗しました"));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleCreateStudent(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusyKey("create-student");
    setLoadError(null);
    setFeedbackMessage(null);

    if (!normalizeStudentNamePart(studentForm.familyName) || !normalizeStudentNamePart(studentForm.givenName)) {
      setBusyKey(null);
      setLoadError(`${tenantLabels.student}の姓と名を入力してください。`);
      return;
    }

    if (studentForm.familyAccountMode === "createNewFamilyAccount" && !studentForm.createFamilyAccountDisplayName.trim()) {
      setBusyKey(null);
      setLoadError(`新しい${tenantLabels.familyAccount}名を入力してください。`);
      return;
    }

    if (studentForm.familyAccountMode === "useExistingFamilyAccount" && !studentForm.familyAccountId) {
      setBusyKey(null);
      setLoadError(`既存の${tenantLabels.familyAccount}を選択してください。`);
      return;
    }

    try {
      const created = await createAdminStudent(tenantSlug, buildCreateStudentRequest(studentForm));
      setStudentForm(createInitialStudentFormState());
      setHasEditedCreateFamilyAccountDisplayName(false);
      setFeedbackMessage(`${tenantLabels.student}を作成しました: ${created.displayName}`);
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.student}の作成に失敗しました`));
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title={`${tenantLabels.student} / ${tenantLabels.familyAccount}`}
      description={`${tenantLabels.familyAccount}は兄弟姉妹のグループ単位です。代表保護者は未設定でも作成でき、招待利用または承認時に自動設定できます。`}
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>生徒・家族アカウント・メンバーを読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}
        {feedbackMessage ? <p>{feedbackMessage}</p> : null}

        {isSessionReady && hasAdminRole ? (
          <>
            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>家族アカウント作成</h2>
              <form
                onSubmit={(event) => void handleCreateFamilyAccount(event)}
                style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
              >
                <label style={{ display: "grid", gap: 4 }}>
                  <span>家族名</span>
                  <input
                    value={familyAccountForm.displayName}
                    onChange={(event) =>
                      setFamilyAccountForm((current) => ({ ...current, displayName: event.target.value }))
                    }
                    placeholder="佐藤様"
                  />
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>代表保護者</span>
                  <select
                    value={familyAccountForm.primaryContactPlatformUserId}
                    onChange={(event) =>
                      setFamilyAccountForm((current) => ({
                        ...current,
                        primaryContactPlatformUserId: event.target.value
                      }))
                    }
                  >
                    <option value="">未設定</option>
                    {members?.items.map((member) => (
                      <option key={member.platformUserId} value={member.platformUserId}>
                        {getMemberDisplayName(member)} / {member.platformUserId}
                      </option>
                    ))}
                  </select>
                  <span style={{ fontSize: 12, opacity: 0.8 }}>
                    代表保護者は、招待消費または承認時に自動設定できます。既存の代表保護者は自動で上書きされません。
                  </span>
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>ステータス</span>
                  <select
                    value={familyAccountForm.status}
                    onChange={(event) =>
                      setFamilyAccountForm((current) => ({
                        ...current,
                        status: event.target.value === "inactive" ? "inactive" : "active"
                      }))
                    }
                  >
                    <option value="active">{describeStatus("active")}</option>
                    <option value="inactive">{describeStatus("inactive")}</option>
                  </select>
                </label>

                <button type="submit" disabled={busyKey === "create-family-account"}>
                  {busyKey === "create-family-account" ? "作成中..." : "家族アカウントを作成"}
                </button>
              </form>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>生徒作成</h2>
              <form
                onSubmit={(event) => void handleCreateStudent(event)}
                style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
              >
                <label style={{ display: "grid", gap: 4 }}>
                  <span>姓</span>
                  <input
                    value={studentForm.familyName}
                    onChange={(event) =>
                      setStudentForm((current) => ({ ...current, familyName: event.target.value }))
                    }
                    placeholder="湯木"
                  />
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>名</span>
                  <input
                    value={studentForm.givenName}
                    onChange={(event) =>
                      setStudentForm((current) => ({ ...current, givenName: event.target.value }))
                    }
                    placeholder="ひかり"
                  />
                </label>

                <div style={{ display: "grid", gap: 4 }}>
                  <span>表示名</span>
                  <div style={{ padding: "10px 12px", border: "1px solid #e5e5e5", borderRadius: 10 }}>
                    {getStudentDisplayNamePreview(studentForm) || "姓と名を入力すると表示されます。"}
                  </div>
                </div>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>表示名かな</span>
                  <input
                    value={studentForm.displayNameKana}
                    onChange={(event) =>
                      setStudentForm((current) => ({ ...current, displayNameKana: event.target.value }))
                    }
                    placeholder="アンナ レビュー"
                  />
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>生年月日</span>
                  <input
                    type="date"
                    value={studentForm.birthDate}
                    onChange={(event) =>
                      setStudentForm((current) => ({ ...current, birthDate: event.target.value }))
                    }
                  />
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>ステータス</span>
                  <select
                    value={studentForm.status}
                    onChange={(event) =>
                      setStudentForm((current) => ({
                        ...current,
                        status: event.target.value === "inactive" ? "inactive" : "active"
                      }))
                    }
                  >
                    <option value="active">{describeStatus("active")}</option>
                    <option value="inactive">{describeStatus("inactive")}</option>
                  </select>
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>家族アカウントの設定方法</span>
                  <select
                    value={studentForm.familyAccountMode}
                    onChange={(event) =>
                      setStudentForm((current) => ({
                        ...current,
                        familyAccountMode:
                          event.target.value === "useExistingFamilyAccount"
                            ? "useExistingFamilyAccount"
                            : event.target.value === "none"
                              ? "none"
                              : "createNewFamilyAccount"
                      }))
                    }
                  >
                    <option value="createNewFamilyAccount">新しく作成する</option>
                    <option value="useExistingFamilyAccount">既存の家族アカウントを使う</option>
                    <option value="none">今は設定しない</option>
                  </select>
                </label>

                {studentForm.familyAccountMode === "createNewFamilyAccount" ? (
                  <section
                    style={{
                      display: "grid",
                      gap: 12,
                      padding: 12,
                      border: "1px solid #e5e5e5",
                      borderRadius: 12,
                      background: "#fafafa"
                    }}
                  >
                    <strong>新しい家族アカウント</strong>

                    <label style={{ display: "grid", gap: 4 }}>
                      <span>家族名</span>
                      <input
                        value={studentForm.createFamilyAccountDisplayName}
                        onChange={(event) =>
                          {
                            setHasEditedCreateFamilyAccountDisplayName(true);
                            setStudentForm((current) => ({
                              ...current,
                              createFamilyAccountDisplayName: event.target.value
                            }));
                          }
                        }
                        placeholder="湯木様"
                      />
                    </label>

                    <span style={{ fontSize: 12, opacity: 0.8 }}>
                      家族名は姓から自動入力されます。必要に応じて修正できます。
                    </span>
                    <span style={{ fontSize: 12, opacity: 0.8 }}>
                      同じ家族名が複数あっても問題ありません。内部IDで区別します。
                    </span>
                    <span style={{ fontSize: 12, opacity: 0.8 }}>
                      家族アカウントは兄弟姉妹や将来の請求集計単位として使います。代表保護者はここでは未設定でも問題ありません。
                    </span>
                  </section>
                ) : null}

                {studentForm.familyAccountMode === "useExistingFamilyAccount" ? (
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>既存の家族アカウント</span>
                    <select
                      value={studentForm.familyAccountId}
                      onChange={(event) =>
                        setStudentForm((current) => ({ ...current, familyAccountId: event.target.value }))
                      }
                    >
                      <option value="">選択してください</option>
                      {familyAccounts?.items.map((familyAccount) => (
                        <option key={familyAccount.familyAccountId} value={familyAccount.familyAccountId}>
                          {familyAccount.displayName} / {familyAccount.familyAccountId} /{" "}
                          {familyAccount.primaryContactDisplayNameSnapshot ??
                            familyAccount.primaryContactPlatformUserId ??
                            "代表保護者未設定"}
                        </option>
                      ))}
                    </select>
                    <span style={{ fontSize: 12, opacity: 0.8 }}>
                      同名の家族アカウントがあり得るため、内部的には `familyAccountId` で紐付けます。
                    </span>
                  </label>
                ) : null}

                {studentForm.familyAccountMode === "none" ? (
                  <p style={{ margin: 0, fontSize: 12, opacity: 0.8 }}>
                    後から兄弟姉妹や請求管理を行う場合は家族アカウントの設定を推奨します。
                  </p>
                ) : null}

                <label style={{ display: "grid", gap: 4 }}>
                  <span>内部メモ</span>
                  <textarea
                    value={studentForm.notesInternal}
                    onChange={(event) =>
                      setStudentForm((current) => ({ ...current, notesInternal: event.target.value }))
                    }
                    rows={3}
                    placeholder="内部メモ"
                  />
                </label>

                <button type="submit" disabled={busyKey === "create-student"}>
                  {busyKey === "create-student" ? "作成中..." : "生徒を作成"}
                </button>
              </form>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>生徒一覧</h2>
              {students?.items.length === 0 ? <p style={{ margin: 0 }}>生徒はまだありません。</p> : null}
              <div style={{ display: "grid", gap: 12 }}>
                {students?.items.map((student) => (
                  <Link
                    key={student.studentId}
                    to={`/t/${tenantSlug}/teacher/admin/students/${student.studentId}`}
                    style={{
                      display: "grid",
                      gap: 6,
                      padding: 12,
                      border: "1px solid #e5e5e5",
                      borderRadius: 12,
                      textDecoration: "none",
                      color: "inherit"
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                      <strong>{student.displayName}</strong>
                      <span style={{ fontSize: 12, opacity: 0.8 }}>{describeStatus(student.status)}</span>
                    </div>
                    <div style={{ fontSize: 14, opacity: 0.8 }}>
                      {student.memberCode} / {student.birthDate}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                      {student.familyAccountDisplayName ?? "家族アカウント未設定"} / {student.studentId}
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>家族アカウント一覧</h2>
              {familyAccounts?.items.length === 0 ? <p style={{ margin: 0 }}>家族アカウントはまだありません。</p> : null}
              <div style={{ display: "grid", gap: 12 }}>
                {familyAccounts?.items.map((familyAccount) => (
                  <Link
                    key={familyAccount.familyAccountId}
                    to={`/t/${tenantSlug}/teacher/admin/family-accounts/${familyAccount.familyAccountId}`}
                    style={{
                      display: "grid",
                      gap: 8,
                      padding: 12,
                      border: "1px solid #e5e5e5",
                      borderRadius: 12,
                      textDecoration: "none",
                      color: "inherit"
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                      <strong>{familyAccount.displayName}</strong>
                      <span style={{ fontSize: 12, opacity: 0.75 }}>詳細/編集</span>
                    </div>
                    <div style={{ fontSize: 14, opacity: 0.8 }}>
                      {describeStatus(familyAccount.status)} / 代表保護者:{" "}
                      {familyAccount.primaryContactDisplayNameSnapshot ??
                        familyAccount.primaryContactPlatformUserId ??
                        "未設定"}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>{familyAccount.familyAccountId}</div>
                  </Link>
                ))}
              </div>
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
